/**
 * Terminal Coverage Calculator — v2 (Strauss "פירוט כיסוי קקאו" table)
 *
 * Deliberately simple, mirroring how Strauss build their own table:
 *
 *   Columns: one per terminal. With a consumption lead (Strauss = 2) a terminal covers the
 *   consumption of its window months + lead (Dec terminal → Dec–Feb), and the first column is
 *   the terminal whose consumption covers the current month (even if its contract expired).
 *
 *   Required contracts (per material, then × ratio → beans, ÷ 10):
 *     current terminal : consumption + emergency stock − physical stock (latest count, as is)
 *     next terminals   : consumption + emergency stock − emergency stock of the previous terminal
 *                        (= we assume the previous terminal was covered and left exactly its
 *                        emergency stock — no stock roll-forward, no projected inventory)
 *     never below 0.
 *
 *   Priced contracts = exchange contracts maturing in the terminal (options counted as contracts)
 *                    + fixed POs delivered in the terminal's consumption months, from the current
 *                      month on (a PO whose delivery date passed is assumed delivered → it is
 *                      already part of the physical stock). Ratio POs are not priced.
 *   Gap = priced − required · Coverage = priced ÷ required.
 *   Avg exchange price: qty-weighted entry price; options at their strike price.
 *   Closed ratio ("רשיו סגור"): qty-weighted ratio of ratio POs delivered in the terminal.
 *
 * Inputs per terminal (demand, emergency stock, physical stock, ratio) come from the backend
 * derivation allocations: GET /api/v2/hedge-derivation/:accountId/derived-hedges.
 */

import { HedgePosition } from '../../../sp-types/positions';
import { TerminalAllocation } from '../../../services/hedgesService';
import { FxConverter } from './StrategiesUtils';

// ─── Output Types ────────────────────────────────────────────────────────────

export interface TerminalMonth {
  label: string;           // Hebrew month name
  year: number;
  month: number;           // 1-based
  demandMT: number;        // product demand (MT) for this month — plain, NO ratio
}

/** Per-material breakdown of one terminal — drives the "פירוט חישוב" view. */
export interface TerminalMaterialCalc {
  formulaCode: string;
  demandMT: number;        // consumption in the terminal
  bufferMT: number;        // emergency stock at the end of the terminal
  availableMT: number;     // current terminal: physical stock · next: previous emergency stock
  netMT: number;           // max(0, demand + buffer − available)
  ratio: number;
  requiredBeansMT: number; // netMT × ratio
  posMT: number;           // fixed POs delivered in the terminal
  posBeansMT: number;      // posMT × ratio
}

/** An exchange position counted in a terminal (for the cell explanations). */
export interface TerminalHedge {
  maturityDate: string;
  quantityMT: number;      // signed (+ long, − short)
  price: number;           // entry price used in the average (options: strike)
  isOption: boolean;
}

export interface TerminalData {
  label: string;           // contract month name, e.g. "ספטמבר '26"
  code: string;            // futures contract code, e.g. "U26"
  windowSpan: string;      // consumption months covered, e.g. "אוקטובר–נובמבר '26"
  startDate: string;
  endDate: string;
  isCurrent: boolean;      // first column — uses the physical stock
  months: TerminalMonth[];
  materials: TerminalMaterialCalc[];
  // Plain product MT (summed across materials, NO ratio)
  demandMT: number;
  bufferMT: number;
  inventoryMT: number;     // available stock at the start (see TerminalMaterialCalc.availableMT)
  posMT: number;
  // Beans (MT); ÷10 for contracts
  requiredMT: number;
  actualMT: number;        // exchange contracts (futures + options)
  posBeansMT: number;
  pricedMT: number;        // actualMT + posBeansMT
  gapMT: number;           // priced − required (negative = under-hedged)
  avgPrice: number;
  avgCurrency: string;
  agreedRatio: number;     // 0 = none → '—'
  coveragePct: number | null; // priced / required × 100; Infinity if required ≤ 0
  hedges: TerminalHedge[];        // exchange positions counted in this terminal
  ratioPOs: RatioPOEntry[];       // ratio POs behind "רשיו סגור"
  inTotal12: number;              // share (0..1) of this terminal inside the 12-month required total
}

export interface TerminalCoverageData {
  formulaCode: string;
  terminals: TerminalData[];
  totalData: {
    demandMT: number;
    requiredMT: number;
    /** Required contracts over the first 12 consumption months only (as in the Strauss table). */
    requiredMT12: number;
    actualMT: number;
    posBeansMT: number;
    pricedMT: number;      // full horizon
    gapMT: number;         // priced − required12
    avgPrice: number;
    avgCurrency: string;
    agreedRatio: number;
    coveragePct: number | null;
  };
  allMonths: TerminalMonth[];
}

// ─── Input Params ────────────────────────────────────────────────────────────

export interface RatioPOEntry {
  formulaCode: string;
  deliveryPeriod: string;  // ISO date
  quantity: number;        // MT
  ratio: number;           // agreed ratio (e.g. 1.80)
}

export interface FixedPOEntry {
  formulaCode: string;
  deliveryPeriod: string;  // ISO date
  quantity: number;        // MT
}

interface CalculateParams {
  formulaCode: string;               // base formula code (e.g. "LCC")
  allocations: TerminalAllocation[]; // from backend derivation API
  hedgePositions: HedgePosition[];   // base formula hedges (raw, not derived)
  /** Real per-month demand (product MT, summed across materials), keyed `${year}-${month1}`. */
  monthlyDemand?: Record<string, number>;
  baseCurrency?: string;
  /** Converts an amount to baseCurrency; used only when a terminal's hedges mix currencies. */
  fxConvert?: FxConverter;
  ratioPOs?: RatioPOEntry[];
  fixedPOs?: FixedPOEntry[];
  /** Consumption lead the allocations were computed with (backend `consumptionLeadMonths`). */
  consumptionLeadMonths?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const HEBREW_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];
const MONTH_CODES = ['F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'];

/** {year, month1} of an ISO date — parses YYYY-MM directly (no UTC/local drift). */
const ym = (iso: string) => { const [y, m] = String(iso).slice(0, 7).split('-').map(Number); return { y, m }; };
const monthIdx = (iso: string) => { const { y, m } = ym(iso); return y * 12 + m - 1; };

const contractCode = (endDate: string) => { const { y, m } = ym(endDate); return `${MONTH_CODES[m - 1]}${String(y).slice(2)}`; };
const contractLabel = (endDate: string) => { const { y, m } = ym(endDate); return `${HEBREW_MONTHS[m - 1]} '${String(y).slice(2)}`; };

function windowSpan(months: TerminalMonth[]): string {
  if (!months.length) return '';
  const first = months[0];
  const last = months[months.length - 1];
  const lastYY = String(last.year).slice(2);
  if (months.length === 1) return `${HEBREW_MONTHS[first.month - 1]} '${lastYY}`;
  if (first.year !== last.year) {
    return `${HEBREW_MONTHS[first.month - 1]} '${String(first.year).slice(2)}–${HEBREW_MONTHS[last.month - 1]} '${lastYY}`;
  }
  return `${HEBREW_MONTHS[first.month - 1]}–${HEBREW_MONTHS[last.month - 1]} '${lastYY}`;
}

/** Consumption months of a terminal window (window months shifted `lead` months later). */
function consumptionMonths(startDate: string, endDate: string, lead: number): Array<{ year: number; month1: number; label: string }> {
  const out: Array<{ year: number; month1: number; label: string }> = [];
  for (let i = monthIdx(startDate) + lead; i <= monthIdx(endDate) + lead; i++) {
    const year = Math.floor(i / 12), m0 = i % 12;
    out.push({ year, month1: m0 + 1, label: HEBREW_MONTHS[m0] });
  }
  return out;
}

/** Entry price of a hedge for the average: options at their strike, futures at the trade price. */
const hedgePrice = (h: HedgePosition) =>
  Number(h.instrument_type === 'option' && h.strike_price ? h.strike_price : (h.underlying_price || h.formula_price || 0));

/** Qty-weighted average price; mixed currencies are converted to the base currency. */
function weightedAvgPrice(
  entries: Array<{ qty: number; price: number; ccy: string }>,
  baseCurrency: string,
  fxConvert?: FxConverter,
): { price: number; currency: string } {
  if (!entries.length) return { price: 0, currency: baseCurrency };
  const currencies = new Set(entries.map(e => e.ccy));
  const mixed = currencies.size > 1;
  let num = 0, den = 0;
  for (const e of entries) {
    num += e.qty * (mixed && fxConvert ? fxConvert(e.price, e.ccy) : e.price);
    den += e.qty;
  }
  return { price: den > 0 ? num / den : 0, currency: mixed ? baseCurrency : (currencies.values().next().value ?? baseCurrency) };
}

function weightedAvgRatio(entries: RatioPOEntry[]): number {
  let num = 0, den = 0;
  for (const e of entries) if (e.ratio > 0 && e.quantity > 0) { num += e.quantity * e.ratio; den += e.quantity; }
  return den > 0 ? num / den : 0;
}

// ─── Main ────────────────────────────────────────────────────────────────────

export function calculateTerminalCoverage({
  formulaCode,
  allocations,
  hedgePositions,
  monthlyDemand,
  baseCurrency = 'USD',
  fxConvert,
  ratioPOs = [],
  fixedPOs = [],
  consumptionLeadMonths = 0,
}: CalculateParams): TerminalCoverageData {
  const base = baseCurrency.toUpperCase();
  const relevantHedges = hedgePositions.filter(h => h.formula_code === formulaCode);
  const now = new Date();
  const currentMonthIdx = now.getFullYear() * 12 + now.getMonth();

  const priceEntriesOf = (hs: HedgePosition[]) => hs.flatMap(h => {
    const qty = Math.abs(Number(h.hedged_quantity_mt) || 0);
    const price = hedgePrice(h);
    return price > 0 && qty > 0 ? [{ qty, price, ccy: (h.contract_currency || h.hedge_currency || base).toUpperCase() }] : [];
  });

  const allMonths: TerminalMonth[] = [];
  const terminals: TerminalData[] = [];
  const prevBuffer: Record<string, number> = {};
  let prevEnd: Date | null = null;

  allocations.forEach((alloc, tIdx) => {
    const isCurrent = tIdx === 0;
    const cms = consumptionMonths(alloc.startDate, alloc.endDate, consumptionLeadMonths);
    const cmIdx = new Set(cms.map(c => c.year * 12 + c.month1 - 1));

    // Per material: required (net need × ratio) and fixed POs delivered in the terminal.
    const materials: TerminalMaterialCalc[] = Object.entries(alloc.materials).map(([fc, m]) => {
      const availableMT = isCurrent ? m.startStorage : (prevBuffer[fc] ?? 0);
      const netMT = Math.max(0, m.demand + m.buffer - availableMT);
      const posMT = fixedPOs
        .filter(p => p.formulaCode === fc && p.deliveryPeriod && cmIdx.has(monthIdx(p.deliveryPeriod)) && monthIdx(p.deliveryPeriod) >= currentMonthIdx)
        .reduce((s, p) => s + Math.abs(Number(p.quantity) || 0), 0);
      return {
        formulaCode: fc, demandMT: m.demand, bufferMT: m.buffer, availableMT, netMT, ratio: m.ratio,
        requiredBeansMT: netMT * m.ratio, posMT, posBeansMT: posMT * m.ratio,
      };
    });
    materials.forEach(m => { prevBuffer[m.formulaCode] = m.bufferMT; });

    const allocDemand = Object.values(alloc.materials).reduce((s, m) => s + m.demand, 0);
    const months: TerminalMonth[] = cms.map(c => {
      const real = monthlyDemand?.[`${c.year}-${c.month1}`];
      // No per-month data at all → even split of the terminal total; otherwise a missing month is 0.
      const demandMT = monthlyDemand ? (real ?? 0) : allocDemand / (cms.length || 1);
      return { label: c.label, year: c.year, month: c.month1, demandMT };
    });
    allMonths.push(...months);

    // Exchange contracts maturing in (previous terminal end, this terminal end].
    const termEnd = new Date(alloc.endDate);
    const periodHedges = relevantHedges.filter(h => {
      if (!h.maturity_date) return false;
      const d = new Date(h.maturity_date);
      return (prevEnd === null || d > prevEnd) && d <= termEnd;
    });
    prevEnd = termEnd;
    const actualMT = periodHedges.reduce((s, h) => s + (Number(h.hedged_quantity_mt) || 0) * (h.position_side === 'long' ? 1 : -1), 0);
    const { price: avgPrice, currency: avgCurrency } = weightedAvgPrice(priceEntriesOf(periodHedges), base, fxConvert);

    const sum = (f: (_m: TerminalMaterialCalc) => number) => materials.reduce((s, m) => s + f(m), 0);
    const requiredMT = sum(m => m.requiredBeansMT);
    const posBeansMT = sum(m => m.posBeansMT);
    const pricedMT = actualMT + posBeansMT;
    const termRatioPOs = ratioPOs.filter(p => p.deliveryPeriod && cmIdx.has(monthIdx(p.deliveryPeriod)));

    terminals.push({
      label: contractLabel(alloc.endDate),
      code: contractCode(alloc.endDate),
      windowSpan: windowSpan(months),
      startDate: alloc.startDate,
      endDate: alloc.endDate,
      isCurrent,
      months,
      materials,
      demandMT: months.reduce((s, m) => s + m.demandMT, 0),
      bufferMT: sum(m => m.bufferMT),
      inventoryMT: sum(m => m.availableMT),
      posMT: sum(m => m.posMT),
      requiredMT,
      actualMT,
      posBeansMT,
      pricedMT,
      gapMT: pricedMT - requiredMT,
      avgPrice,
      avgCurrency,
      agreedRatio: weightedAvgRatio(termRatioPOs),
      coveragePct: requiredMT > 0 ? (pricedMT / requiredMT) * 100 : Infinity,
      hedges: periodHedges.map(h => ({
        maturityDate: String(h.maturity_date).slice(0, 10),
        quantityMT: (Number(h.hedged_quantity_mt) || 0) * (h.position_side === 'long' ? 1 : -1),
        price: hedgePrice(h),
        isOption: h.instrument_type === 'option',
      })),
      ratioPOs: termRatioPOs,
      inTotal12: 0,
    });
  });

  // Total: required over the first 12 consumption months (pro-rated inside the terminal that
  // crosses month 12, by its monthly demand); priced over the full horizon.
  let monthCount = 0, requiredMT12 = 0;
  for (const t of terminals) {
    const n = t.months.length;
    const inside = Math.max(0, Math.min(n, 12 - monthCount));
    if (inside === n) t.inTotal12 = 1;
    else if (inside > 0) {
      const d12 = t.months.slice(0, inside).reduce((s, m) => s + m.demandMT, 0);
      t.inTotal12 = t.demandMT > 0 ? d12 / t.demandMT : inside / n;
    }
    requiredMT12 += t.requiredMT * t.inTotal12;
    monthCount += n;
  }
  const totalPriced = terminals.reduce((s, t) => s + t.pricedMT, 0);
  const { price: totalAvgPrice, currency: totalAvgCurrency } = weightedAvgPrice(priceEntriesOf(relevantHedges), base, fxConvert);

  return {
    formulaCode,
    terminals,
    allMonths,
    totalData: {
      demandMT: terminals.reduce((s, t) => s + t.demandMT, 0),
      requiredMT: terminals.reduce((s, t) => s + t.requiredMT, 0),
      requiredMT12,
      actualMT: terminals.reduce((s, t) => s + t.actualMT, 0),
      posBeansMT: terminals.reduce((s, t) => s + t.posBeansMT, 0),
      pricedMT: totalPriced,
      gapMT: totalPriced - requiredMT12,
      avgPrice: totalAvgPrice,
      avgCurrency: totalAvgCurrency,
      agreedRatio: weightedAvgRatio(ratioPOs),
      coveragePct: requiredMT12 > 0 ? (totalPriced / requiredMT12) * 100 : Infinity,
    },
  };
}
